import { Refresh } from "@mui/icons-material";
import {
  Container,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  FormLabel,
  FormControl
} from "@mui/material";
import {
  Bookmark20Px,
  ConfirmationModal,
  globalDateTimeFormat,
  useToast
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { useGridSelector } from "~/features/inventory/useGridSelector";
import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";
import {
  useMixpanelPageName,
  mixpanelTrack,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";

import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useDeleteBinFlagMutation } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetBinsByFlaggedReasonQuery } from "~/redux/warehouse/bins.openApi";

interface EscalatedBins {
  viewTitle: ViewNameTranslation;
}

export const EscalatedBins: React.FC<EscalatedBins> = (props) => {
  const { viewTitle } = props;
  useNavbar({ viewTitle });
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();
  const [unflagBinNumber, setUnflagBinNumber] = useState<number | null>(null);
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Unflag This Bin");
  const { autostoreGridOverride: autostoreGridId, gridSelectorButton } =
    useGridSelector({ id: "grid-selector" });

  const [deleteBinFlag, { isLoading: isDeletingFlag }] =
    useDeleteBinFlagMutation();

  const {
    data: flaggedBins,
    error,
    isLoading,
    refetch: refetchFlaggedBins,
    fulfilledTimeStamp
  } = useGetBinsByFlaggedReasonQuery(
    autostoreGridId
      ? {
          autostoreGridId,
          flaggedReason: "exterior maintenance"
        }
      : skipToken
  );

  if (error) {
    errorToast(getMessageFromRtkError(error), {
      trackingLocation: trackedModalName
    });
  }

  const handleUnflagBin = async (binNumber: number) => {
    if (!autostoreGridId || !selectedWorkstation) return;
    try {
      await deleteBinFlag({
        autostoreGridId,
        binNumber,
        reason: "exterior maintenance",
        workstationId: selectedWorkstation.id
      }).unwrap();
      successToast(t("flag removed"));
      await refetchFlaggedBins();
    } catch (error) {
      errorToast(getMessageFromRtkError(error), {
        trackingLocation: trackedModalName
      });
    }
  };

  return (
    <>
      <Container maxWidth="md" sx={{ p: 4 }}>
        <Stack direction="column" pb={1}>
          <Typography variant="h6" sx={{ py: 1 }}>
            {t("bins marked for exterior maintenance")}
          </Typography>
          <Stack direction="row" justifyContent="space-between" pb={1}>
            <FormControl>
              <FormLabel htmlFor="grid-selector">
                {`${t("autostore grid")}`}
              </FormLabel>
              {gridSelectorButton}
            </FormControl>
            <Stack direction="row" spacing={2}>
              <Stack>
                <Typography variant="subtitle1">
                  {t("last updated") + ":"}
                </Typography>
                <Typography variant="body2">
                  {dayjs(fulfilledTimeStamp).format("lll")}
                </Typography>
              </Stack>
              <Button
                startIcon={<Refresh />}
                variant="subtle"
                onClick={async () => {
                  mixpanelTrack({
                    trackedPageName,
                    label: "Refresh Escalated Bins",
                    type: "Button Click"
                  });
                  await refetchFlaggedBins();
                }}
              >
                {t("refresh")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">{t("bin id")}</TableCell>
              <TableCell align="left">{t("date escalated")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Skeleton role="progressbar" variant="text" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              autostoreGridId &&
              flaggedBins?.map((binData) => (
                <TableRow key={binData.autostoreBinId}>
                  <TableCell align="left">{binData.autostoreBinId}</TableCell>
                  <TableCell align="left">
                    {dayjs(binData.flaggedTime)
                      .tz(usersFulfillmentCenter?.timeZone ?? undefined)
                      .format(globalDateTimeFormat)}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="subtle"
                      startIcon={<Bookmark20Px />}
                      onClick={() => {
                        mixpanelTrack({
                          trackedPageName,
                          label: "Unflag Bin",
                          type: "Button Click",
                          eventProperties: {
                            "Bin Number": binData.autostoreBinId
                          }
                        });
                        setUnflagBinNumber(binData.autostoreBinId);
                      }}
                      disabled={isDeletingFlag}
                    >
                      {t("unflag")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Container>
      <ConfirmationModal
        isOpen={!!unflagBinNumber}
        labelledById="unflag-this-bin-modal"
        confirmCb={async () => {
          if (unflagBinNumber) {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Confirm",
              type: "Button Click",
              eventProperties: {
                "Bin Number": unflagBinNumber
              }
            });
            await handleUnflagBin(unflagBinNumber);
          }
          setUnflagBinNumber(null);
        }}
        closeCb={() => {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            label: "Close",
            type: "Button Click"
          });
          setUnflagBinNumber(null);
        }}
        modalTitle={t("unflag this bin")}
        modalText={t(
          "please confirm the escalated bin is ready to be used again"
        )}
      />
    </>
  );
};
